import { useState } from 'react';
import { Link } from 'react-router-dom';
import { featuredProducts } from '../../../mocks/products';

export default function ProductShowcase() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerView = 4;

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => 
      Math.min(featuredProducts.length - itemsPerView, prev + 1)
    );
  };

  return (
    <section className="section bg-gradient-subtle">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="mb-4">
            Featured Research Compounds
          </h2>
          <p className="text-lg text-navy-600 max-w-2xl mx-auto">
            High-purity peptides with third-party verification and COA documentation available.
          </p>
        </div>

        {/* Desktop Carousel */}
        <div className="hidden lg:block relative">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out gap-6"
              style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
            >
              {featuredProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex-shrink-0 w-[calc(25%-18px)]"
                  data-product-shop
                >
                  <div className="card card-hover h-full flex flex-col group">
                    {/* Product Image */}
                    <div className="relative w-full aspect-square overflow-hidden bg-gray-50 p-8 rounded-t-xl">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="card-body flex-1 flex flex-col">
                      <h3 className="text-lg font-bold text-navy-900 mb-2">
                        {product.name}
                      </h3>
                      <p className="text-sm text-navy-600 mb-3">
                        {product.specification}
                      </p>
                      <div className="mb-4">
                        <span className="badge badge-success">
                          {product.purity} Pure
                        </span>
                      </div>
                      <p className="text-xs text-red-600 font-medium mb-4 flex items-center">
                        <i className="ri-alert-line mr-1.5"></i>
                        Research Use Only
                      </p>

                      {/* Buttons */}
                      <div className="mt-auto space-y-2.5">
                        <Link
                          to={`/product/${product.slug}`}
                          className="btn btn-primary w-full btn-sm"
                        >
                          Request Quote
                        </Link>
                        <Link
                          to={`/product/${product.slug}`}
                          className="btn btn-outline w-full btn-sm"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-card hover:shadow-card-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all border border-gray-200"
            aria-label="Previous products"
          >
            <i className="ri-arrow-left-line text-xl text-navy-900"></i>
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex >= featuredProducts.length - itemsPerView}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-card hover:shadow-card-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all border border-gray-200"
            aria-label="Next products"
          >
            <i className="ri-arrow-right-line text-xl text-navy-900"></i>
          </button>
        </div>

        {/* Mobile Grid */}
        <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-6" data-product-shop>
          {featuredProducts.slice(0, 4).map((product) => (
            <div
              key={product.id}
              className="card card-hover"
            >
              <div className="relative w-full aspect-square overflow-hidden bg-gray-50 p-8 rounded-t-xl">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="card-body">
                <h3 className="text-lg font-bold text-navy-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-sm text-navy-600 mb-3">
                  {product.specification}
                </p>
                <div className="mb-4">
                  <span className="badge badge-success">
                    {product.purity} Pure
                  </span>
                </div>
                <p className="text-xs text-red-600 font-medium mb-4 flex items-center">
                  <i className="ri-alert-line mr-1.5"></i>
                  Research Use Only
                </p>
                <div className="space-y-2.5">
                  <Link
                    to={`/product/${product.slug}`}
                    className="btn btn-primary w-full btn-sm"
                  >
                    Request Quote
                  </Link>
                  <Link
                    to={`/product/${product.slug}`}
                    className="btn btn-outline w-full btn-sm"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/shop"
            className="btn btn-secondary btn-lg"
          >
            View Complete Catalog
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}